import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'

import {
  SessionContext,
  type Session,
  type SessionStatus,
  type SessionUser,
} from './session-context.js'

/**
 * Who is playing, if anyone.
 *
 * The endpoint lands in DOL-10. Until then it 404s, which this treats the same
 * as a 401 — anonymous. That keeps the shell honest rather than pretending
 * someone is logged in, and means nothing here changes when auth arrives.
 */
export function SessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<SessionStatus>('loading')
  const [user, setUser] = useState<SessionUser | null>(null)

  const refresh = useCallback(async () => {
    try {
      const response = await fetch('/api/auth/me', { credentials: 'same-origin' })

      if (!response.ok) {
        setUser(null)
        setStatus('anonymous')
        return
      }

      setUser((await response.json()) as SessionUser)
      setStatus('authenticated')
    } catch {
      // Offline, or the server is down. Anonymous is the safe assumption —
      // never fail open into a logged-in state.
      setUser(null)
      setStatus('anonymous')
    }
  }, [])

  useEffect(() => {
    // Fetching the session on mount is the intended use of an effect. The rule
    // guards against render loops caused by synchronous setState; here the
    // writes happen in a promise continuation and `refresh` is stable, so this
    // runs exactly once.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void refresh()
  }, [refresh])

  const value = useMemo<Session>(() => ({ status, user, refresh }), [status, user, refresh])

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}
